// src/pages/SalarySlipRequestsPage.js
import React, { useState, useEffect, useMemo } from "react";
import "./SalarySlipRequestReview.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

/* ================= HELPERS ================= */
const normalizeStatus = (status) =>
  (status || "").toString().trim().toUpperCase();

const SalarySlipRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  /* ================= STATUS PRIORITY ================= */
  const statusPriority = useMemo(
    () => ({
      PENDING: 1,
      APPROVED: 2,
      REJECTED: 3,
    }),
    []
  );

  /* ================= FETCH REQUESTS ================= */
  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const data = await apiClient("/salary-slip/list/");
      if (!data) return;

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.results)
        ? data.results
        : [];

      setRequests(list);
      setFilteredRequests(list);
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to load salary slip requests.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= FILTER + SORT ================= */
  useEffect(() => {
    const filtered = requests
      .filter((req) => {
        const status = normalizeStatus(req.status);

        const matchStatus =
          statusFilter === "" ||
          status === normalizeStatus(statusFilter);

        const matchEmployee =
          employeeFilter === "" ||
          req.employee_name
            ?.toLowerCase()
            .includes(employeeFilter.toLowerCase());

        const matchDepartment =
          departmentFilter === "" ||
          req.employee_department
            ?.toLowerCase()
            .includes(departmentFilter.toLowerCase());

        return matchStatus && matchEmployee && matchDepartment;
      })
      .sort(
        (a, b) =>
          (statusPriority[normalizeStatus(a.status)] || 99) -
          (statusPriority[normalizeStatus(b.status)] || 99)
      );

    setFilteredRequests(filtered);
  }, [
    statusFilter,
    employeeFilter,
    departmentFilter,
    requests,
    statusPriority,
  ]);

  /* ================= FILE PICKER ================= */
  const pickPdfFile = () =>
    new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".pdf";
      input.onchange = () => resolve(input.files[0]);
      input.click();
    });

  /* ================= APPROVE / REJECT ================= */
  const handleAction = async (id, status) => {
    try {
      if (status === "Approved") {
        const file = await pickPdfFile();
        if (!file) return;

        const formData = new FormData();
        formData.append("status", "Approved");
        formData.append("salary_slip", file);

        await apiClient(`/salary-slip/update/${id}/`, {
          method: "POST",
          body: formData,
        });
      } else {
        await apiClient(`/salary-slip/update/${id}/`, {
          method: "POST",
          body: JSON.stringify({ status: "Rejected" }),
        });
      }

      fetchRequests();
    } catch (err) {
      console.error(err);
      alert("Failed to update salary slip request.");
    }
  };

  /* ================= COUNTS ================= */
  const totalCount = requests.length;
  const pendingCount = requests.filter(
    (r) => normalizeStatus(r.status) === "PENDING"
  ).length;
  const approvedCount = requests.filter(
    (r) => normalizeStatus(r.status) === "APPROVED"
  ).length;
  const rejectedCount = requests.filter(
    (r) => normalizeStatus(r.status) === "REJECTED"
  ).length;

  /* ================= UI ================= */
  return (
    <div className="salary-container">
      <h2 className="salary-title">Salary Slip Requests</h2>

      {/* SUMMARY */}
      <div className="request-tiles13">
        <div className="tile13 total">Total: {totalCount}</div>
        <div className="tile13 pending">Pending: {pendingCount}</div>
        <div className="tile13 approved">Approved: {approvedCount}</div>
        <div className="tile13 rejected">Rejected: {rejectedCount}</div>
      </div>

      {/* FILTERS */}
      <div className="salary-filter">
        <label>Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>

        <label>Employee:</label>
        <input
          type="text"
          placeholder="Search employee"
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
        />

        <label>Department:</label>
        <input
          type="text"
          placeholder="Search department"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
        />
      </div>

      {loading && <p>Loading salary slip requests…</p>}
      {errorMsg && <p className="error-message">{errorMsg}</p>}

      {/* TABLE */}
      <div className="table-wrapper">
        <table className="salary-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>From</th>
              <th>To</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredRequests.length > 0 ? (
              filteredRequests.map((req) => {
                const status = normalizeStatus(req.status);

                return (
                  <tr key={req.id}>
                    <td>{req.employee_name}</td>
                    <td>{req.employee_department}</td>
                    <td>{req.from_month}</td>
                    <td>{req.to_month}</td>
                    <td>{req.reason}</td>
                    <td className={`status-${status.toLowerCase()}`}>
                      {status}
                    </td>
                    <td>
                      {status === "PENDING" && (
                        <>
                          <button
                            className="btn-approve"
                            onClick={() =>
                              handleAction(req.id, "Approved")
                            }
                          >
                            Approve
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() =>
                              handleAction(req.id, "Rejected")
                            }
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  No salary slip requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <SessionHandler />
    </div>
  );
};

export default SalarySlipRequestsPage;
